import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const PiiDetectorViewResult = ({result}) => {
  const categoryIcons = {
    personal_identifiers: {
      icon: 'IdentificationCard',
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    },
    contact_information: {
      icon: 'AddressBook',
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    demographic_data: {
      icon: 'Users',
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    financial_information: {
      icon: 'Bank',
      color: 'text-yellow-600',
      bg: 'bg-yellow-100'
    },
    biometric_data: {
      icon: 'Fingerprint',
      color: 'text-red-600',
      bg: 'bg-red-100'
    }
  };

  const subcategoryIcons = {
    // Personal Identifiers
    ssn: 'IdentificationCard',
    passport: 'Passport',
    license: 'Cards',
    tax_id: 'Receipt',
    name: 'User',
    username: 'At',
    family_name: 'Users',
    given_name: 'User',
    
    // Contact Information
    email: 'Envelope',
    phone: 'Phone',
    address: 'MapPin',
    social_media: 'ShareNetwork',
    website: 'Globe',
    ip_address: 'Desktop',
    mac_address: 'Laptop',
    
    // Demographic Data
    age: 'Clock',
    gender: 'Gender',
    nationality: 'Flag',
    ethnicity: 'Users',
    
    // Financial Information
    account_number: 'Bank',
    card_number: 'CreditCard',
    routing_number: 'Bank',
    
    // Biometric Data
    fingerprint: 'Fingerprint',
    facial_data: 'Scan',
    genetic_info: 'Dna'
  };

  const formatEntityRow = (entity) => (
    <div className="mb-2 p-2 bg-base-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {PhosphorIcons[subcategoryIcons[entity.category]] && (
            <div className={`p-1.5 rounded-lg ${categoryIcons[entity.category]?.bg}`}>
              {React.createElement(PhosphorIcons[subcategoryIcons[entity.category]], {
                size: 16,
                weight: "bold",
                className: categoryIcons[entity.category]?.color
              })}
            </div>
          )}
          <span className="font-medium">{entity?.value}</span>
        </div>
        <div className="flex gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs ${
            entity?.risk_level === 'high' ? 'bg-error/20 text-error' :
            entity?.risk_level === 'medium' ? 'bg-warning/20 text-warning' :
            'bg-info/20 text-info'
          }`}>
            {entity?.risk_level}
          </span>
          <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
            {Math.round(entity?.confidence * 100)}%
          </span>
        </div>
      </div>
      {entity?.mentions && entity?.mentions?.length > 0 && (
        <div className="text-sm mt-2 text-base-content/70">
          <span className="font-medium">Found in: </span>
          {entity?.mentions?.join(', ')}
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* PII Categories */}
        <div className="space-y-4">
          {Object.entries(categoryIcons).map(([category, categoryInfo]) => {
            const entities = result?.pii_entities?.[category] || [];
            if (entities.length === 0) return null;

            return (
              <div key={category} className="card bg-base-100 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`p-2 rounded-lg ${categoryInfo.bg}`}>
                      {React.createElement(PhosphorIcons[categoryInfo.icon], {
                        size: 20,
                        weight: "bold",
                        className: categoryInfo.color
                      })}
                    </div>
                    <h3 className="card-title text-lg capitalize">
                      {category.replace(/_/g, ' ')}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {entities?.map((entity, index) => (
                      <div key={index}>
                        {formatEntityRow(entity)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Metadata Summary */}
        <div className="space-y-4">
          {result?.metadata && (
            <>
              {/* Risk Summary Card */}
              <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 rounded-lg bg-red-100">
                      <PhosphorIcons.Warning
                        size={20}
                        weight="bold"
                        className="text-red-600"
                      />
                    </div>
                    <h3 className="card-title text-lg">Risk Summary</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="stat bg-error/10 rounded-lg p-3">
                      <div className="stat-title text-error text-xs">High Risk</div>
                      <div className="stat-value text-error text-2xl">
                        {result?.metadata?.risk_summary?.high_risk_count}
                      </div>
                    </div>
                    <div className="stat bg-warning/10 rounded-lg p-3">
                      <div className="stat-title text-warning text-xs">Medium Risk</div>
                      <div className="stat-value text-warning text-2xl">
                        {result?.metadata?.risk_summary?.medium_risk_count}
                      </div>
                    </div>
                    <div className="stat bg-info/10 rounded-lg p-3">
                      <div className="stat-title text-info text-xs">Low Risk</div>
                      <div className="stat-value text-info text-2xl">
                        {result?.metadata?.risk_summary?.low_risk_count}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Entity Counts & Confidence Card */}
              <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <PhosphorIcons.ChartBar
                        size={20}
                        weight="bold"
                        className="text-blue-600"
                      />
                    </div>
                    <h3 className="card-title text-lg">Analysis Metrics</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Total Entities */}
                    <div className="stat bg-base-200 rounded-lg p-3">
                      <div className="stat-title text-xs">Total Entities Detected</div>
                      <div className="stat-value text-2xl">
                        {result?.metadata?.entity_counts?.total}
                      </div>
                    </div>

                    {/* Category Breakdown */}
                    <div className="space-y-2">
                      {Object.entries(result?.metadata?.entity_counts?.by_category || {}).map(([category, count]) => (
                        <div key={category} className="flex items-center justify-between p-2 bg-base-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg ${categoryIcons[category]?.bg}`}>
                              {React.createElement(PhosphorIcons[categoryIcons[category]?.icon], {
                                size: 16,
                                weight: "bold",
                                className: categoryIcons[category]?.color
                              })}
                            </div>
                            <span className="capitalize">{category.replace(/_/g, ' ')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-xs bg-base-300">
                              {count} entities
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                              {Math.round(result?.metadata?.confidence_summary?.by_category?.[category] * 100)}% conf.
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Average Confidence */}
                    <div className="stat bg-success/10 rounded-lg p-3">
                      <div className="stat-title text-success text-xs">Average Confidence</div>
                      <div className="stat-value text-success text-2xl">
                        {Math.round(result?.metadata?.confidence_summary?.average * 100)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PiiDetectorViewResult